import { Component } from '@theme/component';

const MAX_PILLS = 84;
const SAMPLE_COUNT = 1000;
const COMPACT_BREAKPOINT = 620;

class SnakePathComponent extends Component {
  requiredRefs = ['path'];

  #frame = null;
  #resizeFrame = null;
  #resizeObserver = null;
  #visiblePills = [];
  #lastWidth = 0;
  #lastHeight = 0;
  #randomSeed = Math.random() * 2147483647;

  connectedCallback() {
    super.connectedCallback();
    this.#build();

    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (Math.abs(width - this.#lastWidth) < 1 && Math.abs(height - this.#lastHeight) < 1) return;
      if (this.#resizeFrame !== null) cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = requestAnimationFrame(() => this.#build());
    });
    this.#resizeObserver.observe(this);

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#build();
    });

    if (this.#reducedMotion) {
      this.#settle();
    } else {
      this.#loop();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    if (this.#resizeFrame !== null) cancelAnimationFrame(this.#resizeFrame);
    this.#resizeObserver?.disconnect();
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #allPills() {
    return /** @type {HTMLElement[]} */ ([...this.refs.path.children]);
  }

  #loop = () => {
    this.#paint();
    this.#frame = requestAnimationFrame(this.#loop);
  };

  #build() {
    const rect = this.refs.path.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    this.#lastWidth = this.getBoundingClientRect().width;
    this.#lastHeight = this.getBoundingClientRect().height;
    this.#preparePills();

    const compact = rect.width < COMPACT_BREAKPOINT;
    const randomise = this.getAttribute('data-randomise') !== 'false';
    const rnd = randomise ? seededRandom(this.#randomSeed) : () => 0.5;
    const ratio = rect.height / rect.width;
    const samples = sample(controlPoints(compact, rnd, ratio), SAMPLE_COUNT, ratio);
    const total = samples.at(-1)?.len ?? 0;
    const toUnits = (pixels) => (pixels * 100) / rect.width;
    const gap = toUnits(20);
    const margin = toUnits(26);
    const pills = this.#allPills;
    const metrics = pills.map((pill) => ({
      width: pill.offsetWidth || 140,
      height: pill.offsetHeight || 38,
    }));
    const tangentAt = (length) => {
      const index = indexAt(samples, length);
      const back = samples[Math.max(index - 7, 0)];
      const forward = samples[Math.min(index + 7, samples.length - 1)];
      const angle = (Math.atan2((forward.y - back.y) * ratio, forward.x - back.x) * 180) / Math.PI;
      return { index, angle };
    };

    this.#visiblePills = [];
    let cursor = toUnits(metrics[0].width / 2) + gap + margin;
    let previousAngle = null;

    pills.forEach((pill, index) => {
      const metric = metrics[index];
      const halfWidth = toUnits(metric.width / 2);
      let hit = tangentAt(cursor);

      if (previousAngle !== null) {
        let delta = Math.abs(hit.angle - previousAngle);
        if (delta > 180) delta = 360 - delta;
        if (delta > 6) {
          const clearance = compact ? 1.5 : 0.5;
          cursor += toUnits((Math.min(delta, 90) / 90) * metric.height * clearance);
          hit = tangentAt(cursor);
        }
      }

      if (cursor + halfWidth + margin > total) {
        pill.hidden = true;
        return;
      }

      const point = samples[hit.index];
      pill.hidden = false;
      pill.classList.add('snake-path__pill');
      pill.style.left = `${point.x.toFixed(3)}%`;
      pill.style.top = `${point.y.toFixed(3)}%`;
      pill.style.setProperty('--snake-pill-angle', `${hit.angle.toFixed(2)}deg`);
      pill.style.setProperty('--snake-pill-scale', '0');
      pill.style.opacity = '0';
      this.#visiblePills.push(pill);
      previousAngle = hit.angle;

      const nextMetric = metrics[Math.min(index + 1, metrics.length - 1)];
      cursor += halfWidth + gap + toUnits(nextMetric.width / 2);
    });

    if (this.#reducedMotion) this.#settle();
  }

  #preparePills() {
    this.refs.path.querySelectorAll(':scope > [data-snake-copy]').forEach((copy) => copy.remove());
    const sources = /** @type {HTMLElement[]} */ ([...this.refs.path.children]);
    if (!sources.length) return;

    let index = sources.length;
    while (index < MAX_PILLS) {
      const copy = /** @type {HTMLElement} */ (sources[index % sources.length].cloneNode(true));
      copy.dataset.snakeCopy = '';
      copy.setAttribute('aria-hidden', 'true');
      stripEditorAttributes(copy);
      this.refs.path.append(copy);
      index += 1;
    }
  }

  #paint() {
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight || 800;
    const span = rect.height * 0.6 + viewport * 0.55;
    const progress = clamp((viewport * 0.92 - rect.top) / span, 0, 1);
    const count = this.#visiblePills.length;

    this.#visiblePills.forEach((pill, index) => {
      const start = (index / Math.max(count, 1)) * 0.8;
      const local = clamp((progress - start) / 0.14, 0, 1);
      const scale = local <= 0 ? 0 : overshoot(local);
      pill.style.setProperty('--snake-pill-scale', scale.toFixed(4));
      pill.style.opacity = `${Math.min(1, local * 4)}`;
    });
  }

  #settle() {
    this.#visiblePills.forEach((pill) => {
      pill.style.setProperty('--snake-pill-scale', '1');
      pill.style.opacity = '1';
    });
  }
}

function stripEditorAttributes(root) {
  [root, ...root.querySelectorAll('*')].forEach((element) => {
    element.removeAttribute('id');
    [...element.attributes].forEach((attribute) => {
      if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
    });
  });
}

function controlPoints(compact, rnd, ratio) {
  const jitter = (value, amount) => value + (rnd() - 0.5) * amount;

  const leg = (x0, y0, x1, y1, waves, amplitude, steps) => {
    const points = [];
    const phase = rnd() * Math.PI * 2;
    for (let index = 0; index <= steps; index += 1) {
      const progress = index / steps;
      points.push({
        x: x0 + (x1 - x0) * progress,
        y:
          y0 +
          (y1 - y0) * progress +
          Math.sin(phase + progress * Math.PI * waves) * amplitude +
          Math.sin(progress * Math.PI * waves * 2.3) * amplitude * 0.22,
      });
    }
    return points;
  };

  const uTurn = (cx, cy, halfGap, side, wide) => {
    const points = [];
    const radiusX = halfGap * ratio * wide;
    const direction = side === 'right' ? 1 : -1;
    for (let index = 0; index <= 14; index += 1) {
      const angle = (-90 + (index / 14) * 180) * (Math.PI / 180);
      points.push({
        x: cx + direction * radiusX * Math.cos(angle),
        y: cy + halfGap * Math.sin(angle),
      });
    }
    return points;
  };

  if (compact) {
    const topEdge = jitter(16, 1.4);
    const firstGap = jitter(33, 1.4);
    const secondGap = jitter(50, 1.4);
    const thirdGap = jitter(71, 1.4);
    const lowerTurn = jitter(98, 1.4);

    return [
      ...leg(-18, topEdge, 86, topEdge, 2.4, 2.2, 10),
      ...uTurn(86, (topEdge + firstGap) / 2, (firstGap - topEdge) / 2, 'right', 2.15),
      ...leg(86, firstGap, 14, firstGap, 2.1, 2.1, 9),
      ...uTurn(14, (firstGap + secondGap) / 2, (secondGap - firstGap) / 2, 'left', 2.55),
      ...leg(14, secondGap, 88, secondGap, 2.4, 2.3, 9),
      ...uTurn(88, (secondGap + thirdGap) / 2, (thirdGap - secondGap) / 2, 'right', 2.6),
      ...leg(88, thirdGap, 18, thirdGap, 2.1, 2.5, 9),
      ...uTurn(18, (thirdGap + lowerTurn) / 2, (lowerTurn - thirdGap) / 2, 'left', 2.75),
      ...leg(18, lowerTurn, 142, 118, 3.2, 3.6, 14),
    ];
  }

  const topY = jitter(5.5, 2);
  const middleY = jitter(39.5, 2);
  const lowerY = jitter(67, 2);
  const rightRadius = (middleY - topY) / 2;
  const leftRadius = (lowerY - middleY) / 2;

  return [
    ...leg(-10, topY, 70, topY, 2.6, 3.2, 10),
    ...uTurn(78, (topY + middleY) / 2, rightRadius, 'right', 2.2),
    ...leg(68, middleY, 27, middleY, 2.2, 3, 8),
    ...uTurn(29, (middleY + lowerY) / 2, leftRadius, 'left', 3.4),
    ...leg(31, lowerY + 2, 118, lowerY + 31, 3.6, 4.2, 14),
  ];
}

function sample(points, count, ratio) {
  const pointAt = (index) => points[clamp(index, 0, points.length - 1)];
  const samples = [];

  for (let sampleIndex = 0; sampleIndex < count; sampleIndex += 1) {
    const position = (sampleIndex / (count - 1)) * (points.length - 1);
    const index = Math.min(Math.floor(position), points.length - 2);
    const progress = position - index;
    const [p0, p1, p2, p3] = [pointAt(index - 1), pointAt(index), pointAt(index + 1), pointAt(index + 2)];
    const progress2 = progress * progress;
    const progress3 = progress2 * progress;
    const x =
      0.5 *
      (2 * p1.x +
        (-p0.x + p2.x) * progress +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * progress2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * progress3);
    const y =
      0.5 *
      (2 * p1.y +
        (-p0.y + p2.y) * progress +
        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * progress2 +
        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * progress3);
    const previous = samples.at(-1);
    const length = previous ? previous.len + Math.hypot(x - previous.x, (y - previous.y) * ratio) : 0;
    samples.push({ x, y, len: length });
  }

  return samples;
}

function indexAt(samples, target) {
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (samples[middle].len < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function overshoot(value) {
  const strength = 1.9;
  return 1 + (strength + 1) * (value - 1) ** 3 + strength * (value - 1) ** 2;
}

function clamp(value, minimum, maximum) {
  return value < minimum ? minimum : value > maximum ? maximum : value;
}

if (!customElements.get('snake-path')) {
  customElements.define('snake-path', SnakePathComponent);
}
